import { Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { resolveApiBaseUrl } from '@core/api/api-base-url';
import { AuthService } from '@core/auth/auth.service';
import { ClientDiagnosticsService } from '@core/diagnostics/client-diagnostics.service';

type CaptureState = 'idle' | 'busy' | 'saved' | 'failed';

/** Development-only: saves a PNG of the visible screen to the backend for later review. */
@Component({
  selector: 'app-dev-capture-button',
  standalone: true,
  template: `
    @if (visible) {
      <button
        type="button"
        class="dev-capture"
        data-dev-capture
        data-testid="dev-capture-button"
        [class.is-saved]="state === 'saved'"
        [class.is-failed]="state === 'failed'"
        [disabled]="state === 'busy'"
        [attr.aria-label]="label"
        [title]="label"
        (click)="capture()"
      >
        @switch (state) {
          @case ('saved') {
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          }
          @case ('failed') {
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          }
          @default {
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
          }
        }
      </button>
    }
  `,
  styles: [
    `
      .dev-capture {
        position: fixed;
        z-index: 2000;
        left: 0.75rem;
        bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
        width: 44px;
        height: 44px;
        display: grid;
        place-items: center;
        border: 1px dashed rgba(14, 30, 22, 0.45);
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.85);
        color: #1f6435;
        box-shadow: 0 2px 10px rgba(14, 30, 22, 0.18);
        cursor: pointer;
        opacity: 0.8;
        -webkit-tap-highlight-color: transparent;
      }

      .dev-capture:disabled {
        cursor: progress;
        opacity: 0.45;
      }

      .dev-capture.is-saved {
        opacity: 1;
        border-style: solid;
        border-color: #1f6435;
      }

      .dev-capture.is-failed {
        opacity: 1;
        color: #b42318;
        border-style: solid;
        border-color: #b42318;
      }

      @media (max-width: 860px) {
        .dev-capture {
          bottom: calc(6rem + env(safe-area-inset-bottom, 0px));
        }
      }
    `
  ]
})
export class DevCaptureButtonComponent {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly diagnostics = inject(ClientDiagnosticsService);
  private readonly endpoint = `${resolveApiBaseUrl(
    globalThis.location?.hostname ?? 'localhost',
    environment.apiBaseUrl,
    globalThis.location?.protocol ?? 'http:'
  )}/api/client-screenshots`;
  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  state: CaptureState = 'idle';

  get visible(): boolean {
    return this.diagnostics.enabled && this.auth.isLoggedIn;
  }

  get label(): string {
    switch (this.state) {
      case 'busy':
        return 'שומר צילום מסך…';
      case 'saved':
        return 'צילום המסך נשמר';
      case 'failed':
        return 'שמירת צילום המסך נכשלה';
      default:
        return 'צילום מסך לבדיקה';
    }
  }

  async capture(): Promise<void> {
    if (this.state === 'busy') return;
    this.setState('busy');

    const route = this.diagnostics.currentRoute();
    const viewport = this.diagnostics.viewport();
    try {
      const { default: html2canvas } = await import('html2canvas-pro');
      const canvas = await html2canvas(document.body, {
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scale: Math.min(window.devicePixelRatio || 1, 2),
        useCORS: true,
        logging: false,
        ignoreElements: (element) => element.hasAttribute('data-dev-capture')
      });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Canvas produced no image');

      const form = new FormData();
      form.append('image', blob, 'screen.png');
      form.append('route', route);
      form.append('width', String(viewport.width));
      form.append('height', String(viewport.height));
      if (viewport.visualWidth != null) form.append('visualWidth', String(viewport.visualWidth));
      if (viewport.visualHeight != null) form.append('visualHeight', String(viewport.visualHeight));
      form.append('dpr', String(viewport.dpr));
      form.append('scrollY', String(viewport.scrollY));
      form.append('userAgent', navigator.userAgent.slice(0, 160));

      await firstValueFrom(this.http.post(this.endpoint, form));
      this.setState('saved', 2000);
    } catch (error) {
      this.diagnostics.error('screenshot', error);
      this.setState('failed', 3000);
    }
  }

  private setState(state: CaptureState, resetAfterMs?: number): void {
    if (this.resetTimer) clearTimeout(this.resetTimer);
    this.resetTimer = null;
    this.state = state;
    if (resetAfterMs) {
      this.resetTimer = setTimeout(() => {
        this.state = 'idle';
        this.resetTimer = null;
      }, resetAfterMs);
    }
  }
}
